/**
 * Übersetzungs-Katalog der I18n_Infrastruktur.
 *
 * Enthält die Sprachtypen/-konstanten sowie den flachen, pro Unterstützter_Sprache
 * organisierten Katalog. Übersetzungs-Schlüssel sind punktierte, flache String-Keys
 * mit konzeptionellen Namespaces als Präfix (z. B. `common.`, `errors.`).
 *
 * Jeder Key existiert in beiden Sprachen (`de` und `en`), damit die Fallback-Kette
 * deterministisch bleibt (Req 2.2, 2.3). Weitere Keys werden in späteren Tasks ergänzt.
 */

/** Eine der Unterstützten_Sprachen der Frontend_App. */
export type Language = "de" | "en";

/** Alle Unterstützten_Sprachen (Req 1.1, 1.2). */
export const SUPPORTED_LANGUAGES: readonly Language[] = ["de", "en"];

/** Die Standardsprache / Fallback-Sprache (Req 1.4). */
export const DEFAULT_LANGUAGE: Language = "de";

/**
 * Flache Key→Text-Map je Sprache. Jeder Key existiert in beiden Sprachen
 * (Req 2.2/2.3), damit `translate` zuverlässig auf `de` zurückfallen kann.
 */
export type Catalog = Record<Language, Record<string, string>>;

export const catalog: Catalog = {
  de: {
    // common.* — allgemeine, seitenübergreifende Texte
    "common.appName": "Schoolescort-Planner",
    "common.loading": "Wird geladen …",
    "common.save": "Speichern",
    "common.cancel": "Abbrechen",
    "common.delete": "Löschen",
    "common.edit": "Bearbeiten",
    "common.close": "Schließen",
    "common.back": "Zurück",
    "common.confirm": "Bestätigen",
    "common.retry": "Erneut versuchen",

    // languageSwitcher.* — Sprach-Umschalter
    "languageSwitcher.groupAria": "Sprache wählen",

    // themeModeSwitcher.* — Farbschema-Umschalter
    "themeModeSwitcher.auto": "Farbschema: automatisch (wie System)",
    "themeModeSwitcher.light": "Farbschema: hell",
    "themeModeSwitcher.dark": "Farbschema: dunkel",

    // landing.* — Anmelde-/Registrierungs-Seite
    "landing.tab.login": "Anmelden",
    "landing.tab.register": "Registrieren",
    "landing.field.name": "Name",
    "landing.field.email": "E-Mail",
    "landing.field.password": "Passwort",
    "landing.submit.signingIn": "Anmeldung läuft …",
    "landing.submit.registering": "Registrierung läuft …",
    "landing.submit.signIn": "Anmelden",
    "landing.submit.createAccount": "Konto erstellen",
    "landing.link.whatIs": "Was ist schoolescort?",
    // landing.register.* — Hinweise nach der Registrierung / Verifizierung
    "landing.register.checkEmail": "Bitte bestätige deine E-Mail, wir haben dir einen Link geschickt.",
    "landing.login.emailNotVerified": "Bitte bestätige zuerst deine E-Mail-Adresse, um dich anzumelden.",
    "landing.login.resend": "E-Mail erneut senden",

    // groupList.* — Übersicht der Gruppen
    "groupList.title": "Meine Gruppen",
    "groupList.action.profile": "Profil",
    "groupList.profileSubtitle": "Kinder & Email-Benachrichtigung",
    "groupList.profileAria": "Profil",
    "groupList.action.createGroup": "Gruppe erstellen",
    "groupList.action.joinGroup": "Gruppe beitreten",
    "groupList.action.createHint": "Neue Gruppe anlegen",
    "groupList.action.joinHint": "Mit Einladungscode",
    "groupList.viewPlan": "Plan ansehen",
    "groupList.viewPlanAria": "{name}: Plan ansehen",
    "groupList.loading": "Gruppen werden geladen …",
    "groupList.empty.title": "Noch keine Gruppen",
    "groupList.empty.hint": "Erstelle eine neue Gruppe oder tritt mit einem Einladungscode einer bestehenden bei.",
    "groupList.signOut": "Abmelden",
    // groupList.create.* — Formular zum Erstellen einer Gruppe
    "groupList.create.title": "Neue Gruppe erstellen",
    "groupList.create.nameLabel": "Gruppenname",
    "groupList.create.namePlaceholder": "z. B. Morgengruppe",
    "groupList.create.nameRequired": "Der Gruppenname ist erforderlich.",
    "groupList.create.submitting": "Wird erstellt …",
    "groupList.create.submit": "Gruppe erstellen",
    // groupList.join.* — Formular zum Beitreten einer Gruppe
    "groupList.join.title": "Einer Gruppe beitreten",
    "groupList.join.codeLabel": "Einladungscode",
    "groupList.join.codePlaceholder": "z. B. AB12CD34",
    "groupList.join.codeRequired": "Der Einladungscode ist erforderlich.",
    "groupList.join.submitting": "Beitritt läuft …",
    "groupList.join.submit": "Gruppe beitreten",
    // groupList.defaults.* — Standard-Zusammenfassung auf der Gruppenkarte
    "groupList.defaults.label": "Standard",

    // groupSettings.* — Einstellungen einer Gruppe
    "groupSettings.title": "Gruppeneinstellungen",
    "groupSettings.titleNamed": "{name} — Einstellungen",
    "groupSettings.back": "← Zurück zu den Gruppen",
    "groupSettings.loading": "Gruppe wird geladen …",
    "groupSettings.loadError": "Die Gruppe konnte nicht geladen werden. Bitte aktualisiere die Seite.",
    // groupSettings.invite.* — Einladungscode
    "groupSettings.invite.title": "Einladungscode",
    "groupSettings.invite.copy": "Kopieren",
    "groupSettings.invite.copied": "Kopiert!",
    "groupSettings.invite.copyAria": "Einladungscode kopieren",
    "groupSettings.invite.regenerate": "Code neu erzeugen",
    "groupSettings.invite.regenerating": "Wird neu erzeugt …",
    "groupSettings.invite.hint": "Teile diesen Code, damit andere der Gruppe beitreten können.",
    "groupSettings.invite.hintAdmin": " Beim Neu-Erzeugen wird der vorherige Code ungültig.",
    "groupSettings.invite.regenerateError": "Der Code konnte nicht neu erzeugt werden. Bitte versuche es erneut.",
    // groupSettings.members.* — Mitgliederverwaltung
    "groupSettings.members.title": "Mitglieder ({count})",
    "groupSettings.members.you": " (du)",
    "groupSettings.members.role.admin": "Administrator",
    "groupSettings.members.role.member": "Mitglied",
    "groupSettings.members.remove": "Entfernen",
    "groupSettings.members.removing": "Wird entfernt …",
    "groupSettings.members.removeAria": "{name} entfernen",
    "groupSettings.members.removeError": "Das Mitglied konnte nicht entfernt werden. Bitte versuche es erneut.",
    // groupSettings.delete.* — Gruppe löschen
    "groupSettings.delete.title": "Gruppe löschen",
    "groupSettings.delete.button": "Gruppe löschen",
    "groupSettings.delete.warning": "Diese Gruppe und alle Wochenpläne werden endgültig gelöscht.",
    // groupSettings.retention.* — Aufbewahrung alter Wochenpläne
    "groupSettings.retention.title": "Vergangene Wochenpläne automatisch löschen",
    "groupSettings.retention.immediate": "Sofort",
    "groupSettings.retention.1w": "Nach 1 Woche",
    "groupSettings.retention.1m": "Nach 1 Monat",
    "groupSettings.retention.3m": "Nach 3 Monaten",
    "groupSettings.retention.adminOnlyHint":
      "Nur Admins können diese Einstellung ändern.",
    // groupSettings.notifyOnChange.* — Nachfrage beim Verlassen nach Änderungen
    "groupSettings.notifyOnChange.title": "Nach Änderungen zum Benachrichtigen auffordern",
    "groupSettings.notifyOnChange.label": "Mitglieder beim Verlassen des Wochenplans fragen",
    "groupSettings.notifyOnChange.hint":
      "Wenn aktiviert, wird nach Änderungen am Wochenplan gefragt, ob die Gruppe benachrichtigt werden soll.",
    "groupSettings.notifyOnChange.adminOnlyHint":
      "Nur Admins können diese Einstellung ändern.",
    // groupSettings.defaults.* — Standard-Fahrtdetails der Gruppe
    "groupSettings.defaults.title": "Standard-Fahrtdetails",
    "groupSettings.defaults.hint": "Diese Werte füllen neue Tage und können auf zukünftige Tage übertragen werden.",
    "groupSettings.defaults.morning.title": "Hinweg (Vormittag)",
    "groupSettings.defaults.afternoon.title": "Rückweg (Nachmittag)",
    "groupSettings.defaults.startTime": "Startzeit",
    "groupSettings.defaults.transport": "Transportmittel",
    "groupSettings.defaults.transport.aria": "Standard-Transportmittel für {direction}",
    "groupSettings.defaults.meetingPoint": "Treffpunkt",
    "groupSettings.defaults.save": "Speichern",
    "groupSettings.defaults.saveDialog.title": "Speichern",
    "groupSettings.defaults.saveDialog.overwrite": "Alle zukünftigen Tage überschreiben",
    "groupSettings.defaults.saveDialog.fillEmpty": "Nur leere Felder füllen",
    "groupSettings.defaults.saveDialog.cancel": "Abbrechen",
    "groupSettings.defaults.saved": "Standardwerte gespeichert.",
    "groupSettings.defaults.saveError": "Die Standardwerte konnten nicht gespeichert werden.",

    // profile.* — Profilseite (Benachrichtigungen, Verfügbarkeit, Kinder)
    "profile.title": "Profil",
    "profile.back": "← Zurück zu den Gruppen",
    "profile.groups.loading": "Deine Gruppen werden geladen …",
    "profile.groups.loadError": "Deine Gruppen konnten nicht geladen werden. Bitte aktualisiere die Seite.",
    "profile.groups.empty": "Du bist noch in keiner Gruppe. Tritt einer Gruppe bei oder erstelle eine, um Benachrichtigungen und Verfügbarkeit zu verwalten.",
    // profile.children.* — „Meine Kinder“
    "profile.children.title": "Meine Kinder",
    "profile.children.note": "Verwalte die Kinder, für deren Begleitung du verantwortlich bist.",
    "profile.children.loading": "Deine Kinder werden geladen …",
    "profile.children.loadError": "Deine Kinder konnten nicht geladen werden. Bitte aktualisiere die Seite.",
    "profile.children.empty": "Du hast noch keine Kinder hinzugefügt.",
    "profile.children.newNameAria": "Name des neuen Kindes",
    "profile.children.newNamePlaceholder": "Name des Kindes",
    "profile.children.adding": "Wird hinzugefügt …",
    "profile.children.add": "Kind hinzufügen",
    "profile.children.addError": "Das Kind konnte nicht hinzugefügt werden. Bitte versuche es erneut.",
    "profile.children.nameRequired": "Bitte gib einen Namen ein.",
    // profile.child.* — einzelne Kind-Zeile
    "profile.child.renameAria": "{name} umbenennen",
    "profile.child.saving": "Wird gespeichert …",
    "profile.child.rename": "Umbenennen",
    "profile.child.removing": "Wird entfernt …",
    "profile.child.remove": "Entfernen",
    "profile.child.renameError": "Das Umbenennen ist fehlgeschlagen. Bitte versuche es erneut.",
    "profile.child.removeError": "Das Entfernen ist fehlgeschlagen. Bitte versuche es erneut.",
    "profile.child.nameEmpty": "Der Name darf nicht leer sein.",
    // profile.notifications.* — Benachrichtigungs-Einstellungen
    "profile.notifications.title": "Benachrichtigungen",
    "profile.notifications.note": "Lege pro Gruppe fest, wie du benachrichtigt werden möchtest.",
    "profile.notifications.email": "E-Mail-Benachrichtigungen",
    "profile.notifications.push": "Push-Benachrichtigungen",
    "profile.notifications.saved": "Einstellung gespeichert.",
    "profile.notifications.saveError": "Speichern fehlgeschlagen. Bitte versuche es erneut.",
    "profile.notifications.loading": "Einstellungen werden geladen …",
    "profile.notifications.loadError": "Einstellungen konnten nicht geladen werden.",
    // profile.availability.* — Verfügbarkeit
    "profile.availability.title": "Verfügbarkeit",
    "profile.availability.note": "Markiere Tage, an denen du keine Begleitung übernehmen kannst. Hier hinzugefügte Einträge werden nur für diese Sitzung gespeichert.",
    "profile.availability.dateLabel": "Datum",
    "profile.availability.saving": "Wird gespeichert …",
    "profile.availability.markUnavailable": "Als nicht verfügbar markieren",
    "profile.availability.scheduled": "Eingeplant",
    "profile.availability.remove": "Entfernen",
    "profile.availability.empty": "In dieser Sitzung wurden keine Tage als nicht verfügbar markiert.",
    "profile.availability.pickDate": "Bitte wähle zuerst ein Datum.",
    "profile.availability.alreadyMarked": "Dieses Datum ist bereits als nicht verfügbar markiert.",
    "profile.availability.markError": "Das Datum konnte nicht als nicht verfügbar markiert werden. Bitte versuche es erneut.",
    "profile.availability.removeError": "Der Eintrag konnte nicht entfernt werden. Bitte versuche es erneut.",

    // weeklyPlan.* — Wochenplan-Ansicht
    "weeklyPlan.title": "Wochenplan",
    "weeklyPlan.header.settings": "⚙ Einstellungen & Einladung",
    "weeklyPlan.header.notify": "📣 Gruppe benachrichtigen",
    "weeklyPlan.header.notifyAria": "Gruppe benachrichtigen",
    "weeklyPlan.header.backToGroups": "← Zurück zu den Gruppen",
    // weeklyPlan.nav.* — Wochen-Navigation
    "weeklyPlan.nav.prev": "← Vorherige",
    "weeklyPlan.nav.next": "Nächste →",
    "weeklyPlan.nav.weekLabel": "KW {week}",
    "weeklyPlan.nav.rangeSep": " bis ",
    "weeklyPlan.nav.rangeSepShort": "–",
    // weeklyPlan.* — Lade-/Leer-/Fehlerzustände
    "weeklyPlan.loading": "Plan wird geladen …",
    "weeklyPlan.loadError": "Der Plan konnte nicht geladen werden. Bitte aktualisiere die Seite.",
    "weeklyPlan.empty.title": "Kein Plan für diese Woche",
    "weeklyPlan.empty.hint": "Für {week} gibt es noch keinen Begleitplan.",
    "weeklyPlan.create.submit": "Plan erstellen",
    "weeklyPlan.create.submitting": "Wird erstellt …",
    "weeklyPlan.create.error": "Der Plan konnte nicht erstellt werden. Bitte versuche es erneut.",
    // weeklyPlan.delete.* — Wochenplan löschen
    "weeklyPlan.delete.button": "Woche löschen",
    "weeklyPlan.delete.warning": "Dieser Wochenplan wird endgültig gelöscht.",
    // weeklyPlan.notifyOnChange.* — „Benachrichtigen beim Verlassen"-Prompt
    "weeklyPlan.notifyOnChange.title": "Gruppe benachrichtigen?",
    "weeklyPlan.notifyOnChange.message":
      "Du hast den Wochenplan geändert. Sollen die anderen Mitglieder über die Änderungen informiert werden?",
    "weeklyPlan.notifyOnChange.confirm": "Benachrichtigen",
    "weeklyPlan.notifyOnChange.decline": "Nicht benachrichtigen",
    // weeklyPlan.day.* — Wochentage (Mo–Fr)
    "weeklyPlan.day.mon": "Mo",
    "weeklyPlan.day.tue": "Di",
    "weeklyPlan.day.wed": "Mi",
    "weeklyPlan.day.thu": "Do",
    "weeklyPlan.day.fri": "Fr",
    // weeklyPlan.direction.* — Fahrtrichtungen
    "weeklyPlan.direction.morning": "Vormittag",
    "weeklyPlan.direction.afternoon": "Nachmittag",
    // weeklyPlan.transport.* — Transportmodi (Anzeige; API-Werte bleiben unverändert)
    "weeklyPlan.transport.bike": "🚲 Fahrrad",
    "weeklyPlan.transport.car": "🚗 Auto",
    "weeklyPlan.transport.bus": "🚌 Bus",
    "weeklyPlan.transport.aria": "Transportmittel für {direction}",
    // weeklyPlan.status.* — Verfügbarkeits-Labels
    "weeklyPlan.status.wish": "Ich möchte fahren",
    "weeklyPlan.status.optional": "Ich könnte fahren",
    "weeklyPlan.status.blocker": "Ich kann nicht fahren",
    "weeklyPlan.status.open": "Offen",
    "weeklyPlan.status.clear": "Zurücksetzen",
    "weeklyPlan.status.ownAria": "Deine Verfügbarkeit für {direction}",
    "weeklyPlan.status.memberAria": "Verfügbarkeit für {name} festlegen",
    // weeklyPlan.summary.* — Zusammenfassung (Hero-Box + Zähler)
    "weeklyPlan.summary.wish": "Ich möchte fahren: {count} Fahrten",
    "weeklyPlan.summary.wishFor": "{name} · {count} Fahrten (möchte fahren)",
    "weeklyPlan.summary.sub": "Könnte: {optional} · Kann nicht: {blocker} · Noch offen: {open}",
    // weeklyPlan.admin.* — Admin-Ansicht & „Bearbeiten für"
    "weeklyPlan.admin.view": "Admin-Ansicht",
    "weeklyPlan.admin.editingFor": "Du bearbeitest für {name}",
    "weeklyPlan.admin.end": "Beenden",
    "weeklyPlan.admin.setFor": "Status für {name} gesetzt",
    "weeklyPlan.admin.lockAria": "Vom Admin gesetzt",
    "weeklyPlan.admin.selfLabel": "Ich",
    // weeklyPlan.trip.* — Fahrt-Details
    "weeklyPlan.trip.startTimeAria": "Startzeit für {direction}",
    "weeklyPlan.trip.meetingPoint": "Treffpunkt",
    "weeklyPlan.trip.meetingPointAria": "Treffpunkt für {direction}",
    "weeklyPlan.trip.noResponses": "Noch keine Rückmeldungen",
    "weeklyPlan.you": "Du",
    // weeklyPlan.children.* — Kinder-Bereich
    "weeklyPlan.children.label": "Kinder",
    "weeklyPlan.children.empty": "Noch keine Kinder",
    "weeklyPlan.children.ownAria": "Deine Kinder für {direction}",
    "weeklyPlan.children.add": "＋ {name}",
    "weeklyPlan.children.participating": "✓ {name}",
    "weeklyPlan.children.removeAria": "{name} aus {direction} entfernen",
    "weeklyPlan.children.remove": "Entfernen",
    "weeklyPlan.children.noneHint": "Noch keine Kinder angelegt.",
    "weeklyPlan.children.createLink": "Hier Kinder erstellen",
    // weeklyPlan.manage.* — Admin-Verwaltung
    "weeklyPlan.manage.show": "▸ Mitglieder verwalten",
    "weeklyPlan.manage.hide": "▾ Mitglieder ausblenden",
    "weeklyPlan.manage.noMembers": "Keine aktiven Mitglieder.",
    // weeklyPlan.trip.details — Fahrt-Detail-Panel
    "weeklyPlan.trip.details": "Details",
    "weeklyPlan.trip.detailsAria": "Details für {direction} öffnen",
    "weeklyPlan.trip.who": "Wer fährt mit?",
    "weeklyPlan.trip.passengers": "Mitfahrer",
    "weeklyPlan.trip.onTheWay": "Unterwegs",
    "weeklyPlan.trip.blocked": "Verhindert",
    "weeklyPlan.trip.substitutes": "Vertreter",
    // weeklyPlan.comments.* — Kommentare-Bereich (intern: notes; Anzeige: Kommentare)
    "weeklyPlan.comments.label": "Kommentare",
    "weeklyPlan.comments.empty": "Noch keine Kommentare",
    "weeklyPlan.comments.sectionAria": "Kommentare für {label}",
    "weeklyPlan.comments.openAria": "Kommentare für {label} öffnen",
    "weeklyPlan.comments.deleteAria": "Kommentar löschen",
    "weeklyPlan.comments.delete": "Löschen",
    "weeklyPlan.comments.placeholder": "Kommentar hinzufügen …",
    "weeklyPlan.comments.addAria": "Kommentar für {label} hinzufügen",
    "weeklyPlan.comments.add": "Hinzufügen",
    "weeklyPlan.comments.done": "Fertig",
    // weeklyPlan.notes.* — Notizen-Bereich (intern beibehalten)
    "weeklyPlan.notes.label": "Notizen",
    "weeklyPlan.notes.empty": "Noch keine Notizen",
    "weeklyPlan.notes.sectionAria": "Notizen für {label}",
    "weeklyPlan.notes.deleteAria": "Notiz löschen",
    "weeklyPlan.notes.delete": "Löschen",
    "weeklyPlan.notes.placeholder": "Notiz hinzufügen …",
    "weeklyPlan.notes.addAria": "Notiz für {label} hinzufügen",
    "weeklyPlan.notes.add": "Notiz hinzufügen",
    // weeklyPlan.toast.* — Transiente Erfolgsmeldungen
    "weeklyPlan.toast.modeUpdated": "Transportmittel aktualisiert.",
    "weeklyPlan.toast.detailsUpdated": "Fahrt-Details aktualisiert.",
    "weeklyPlan.toast.childrenUpdated": "Kinder aktualisiert.",
    "weeklyPlan.toast.availabilityUpdated": "Verfügbarkeit aktualisiert.",
    "weeklyPlan.toast.noteAdded": "Notiz hinzugefügt.",
    "weeklyPlan.toast.noteDeleted": "Notiz gelöscht.",
    "weeklyPlan.toast.notified": "Gruppe benachrichtigt.",
    "weeklyPlan.toast.weekDeleted": "Wochenplan gelöscht.",
    "weeklyPlan.toast.notifyError": "Die Gruppe konnte nicht benachrichtigt werden. Bitte versuche es erneut.",

    // editEntry.* — Dialog zum Zuweisen einer Begleitung
    "editEntry.ariaLabel": "Begleitzuweisung bearbeiten",
    "editEntry.title": "Zuweisung bearbeiten",
    "editEntry.loadingMembers": "Mitglieder werden geladen …",
    "editEntry.loadMembersError": "Die zuweisbaren Mitglieder konnten nicht geladen werden.",
    "editEntry.escortLabel": "Begleitung",
    "editEntry.unassigned": "Nicht zugewiesen",
    "editEntry.saving": "Wird gespeichert …",
    "editEntry.saveError": "Die Zuweisung konnte nicht gespeichert werden. Bitte versuche es erneut.",

    // verifyEmail.* — Seite zur E-Mail-Bestätigung (Req 7.1)
    "verifyEmail.title": "E-Mail bestätigen",
    "verifyEmail.loading": "Deine E-Mail-Adresse wird bestätigt …",
    "verifyEmail.success": "Deine E-Mail-Adresse wurde bestätigt. Du kannst dich jetzt anmelden.",
    "verifyEmail.error": "Die Bestätigung ist fehlgeschlagen. Bitte fordere eine neue E-Mail an.",
    "verifyEmail.emailLabel": "E-Mail",
    "verifyEmail.emailPlaceholder": "deine@email.de",
    "verifyEmail.resendButton": "E-Mail erneut senden",
    "verifyEmail.resending": "Wird gesendet …",
    "verifyEmail.resendSnackbar": "Falls ein Konto existiert, haben wir dir eine neue Bestätigungs-E-Mail geschickt.",

    // errors.* — Fehler-Basis inkl. generischem Fallback (Req 12.4/12.5)
    "errors.fallback": "Es ist ein Fehler aufgetreten. Bitte versuche es erneut.",
    "errors.UNAUTHENTICATED": "Bitte melde dich an.",
    "errors.VALIDATION_ERROR": "Die Eingaben sind ungültig. Bitte überprüfe sie.",
    "errors.SERVER_ERROR": "Es ist ein Serverfehler aufgetreten. Bitte versuche es später erneut.",
    // errors.* — abgebildete Backend-Fehler_Codes (Req 12.1/12.2)
    "errors.INVALID_CREDENTIALS": "E-Mail-Adresse oder Passwort ist falsch.",
    "errors.INVALID_TOKEN": "Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.",
    "errors.DUPLICATE_EMAIL": "Diese E-Mail-Adresse ist bereits registriert.",
    "errors.NOT_FOUND": "Der angeforderte Eintrag wurde nicht gefunden.",
    "errors.NOT_GROUP_ADMIN": "Dafür musst du Administrator dieser Gruppe sein.",
    "errors.UNAUTHORIZED_MODIFICATION": "Du bist nicht berechtigt, diese Änderung vorzunehmen.",
    "errors.PAST_TRIP_MODIFICATION": "Vergangene Tage können nicht mehr geändert werden.",
    "errors.PAST_ENTRY_MODIFICATION": "Vergangene Einträge können nicht mehr geändert werden.",
    "errors.INVALID_ISO_WEEK": "Die angegebene Kalenderwoche ist ungültig.",
    "errors.WEEK_OUT_OF_RANGE": "Die gewählte Woche liegt außerhalb des zulässigen Zeitraums.",
    "errors.DUPLICATE_PLAN": "Für diese Woche existiert bereits ein Plan.",
    "errors.INVALID_INVITATION_CODE": "Zu diesem Einladungscode wurde keine Gruppe gefunden.",
    "errors.EMAIL_NOT_VERIFIED": "Bitte bestätige zuerst deine E-Mail-Adresse, um dich anzumelden.",
    "errors.INVALID_VERIFICATION_TOKEN": "Dieser Bestätigungslink ist ungültig oder abgelaufen. Bitte fordere eine neue E-Mail an.",

    // welcome.* — Willkommensseite (/welcome)
    "welcome.logo.alt": "Schoolescort Logo",
    "welcome.nav.howItWorks": "So funktioniert’s",
    "welcome.nav.benefits": "Vorteile",
    "welcome.nav.faq": "FAQ",
    "welcome.nav.join": "Mitmachen",
    "welcome.hero.badge": "Gemeinsam statt allein",
    "welcome.hero.titleBefore": "Der Schulweg wird zum",
    "welcome.hero.titleAccent": "Teamwork.",
    "welcome.hero.subtitle":
      "Mit schoolescort wechseln sich Eltern beim Fahrradbegleiten ab. So kommen Kinder gemeinsam zur Schule – und die Verantwortung verteilt sich auf mehrere Schultern.",
    "welcome.hero.join": "Mit anderen Familien starten",
    "welcome.hero.howItWorks": "So funktioniert’s",
    "welcome.hero.checkNote": "Einfach organisiert · Gemeinsam unterwegs",
    "welcome.how.sectionLabel": "So funktioniert’s",
    "welcome.how.sectionTitle": "Weniger Koordinieren. Mehr gemeinsam radeln.",
    "welcome.how.sectionText":
      "schoolescort bringt Familien zusammen und hilft dabei, die Begleitung fair und übersichtlich zu organisieren.",
    "welcome.how.step1.title": "Eine Gruppe bilden",
    "welcome.how.step1.text":
      "Ein paar Familien aus der Klasse oder Nachbarschaft tun sich zusammen.",
    "welcome.how.step2.title": "Begleitung abstimmen",
    "welcome.how.step2.text":
      "Gemeinsam legt ihr fest, wer an welchem Morgen mit den Kindern fährt.",
    "welcome.how.step3.title": "Zusammen losradeln",
    "welcome.how.step3.text":
      "Die Kinder fahren in einer vertrauten Gruppe sicher und begleitet zur Schule.",
    "welcome.strip.title": "Ein entspannterer Start in den Schultag.",
    "welcome.strip.a.title": "Fair teilen",
    "welcome.strip.a.text": "Die Begleitung wechselt sich ab",
    "welcome.strip.b.title": "Gemeinsam fahren",
    "welcome.strip.b.text": "Kinder erleben den Weg als Gruppe",
    "welcome.strip.c.title": "Klar absprechen",
    "welcome.strip.c.text": "Alle wissen, wer wann dran ist",
    "welcome.benefit.sectionLabel": "Warum schoolescort?",
    "welcome.benefit.sectionTitle": "Gut für Familien. Gut für den Morgen.",
    "welcome.benefit.sectionText":
      "Wenn sich mehrere Familien zusammentun, muss nicht jeden Morgen dieselbe Person begleiten. Aus einer täglichen Aufgabe wird ein gemeinsames Projekt.",
    "welcome.benefit.cta": "Gemeinsam loslegen",
    "welcome.benefit.a.audience": "Für Kinder",
    "welcome.benefit.a.title": "Begleitet unterwegs",
    "welcome.benefit.a.text":
      "Die Kinder fahren in einer vertrauten Gruppe mit einer erwachsenen Begleitperson zur Schule.",
    "welcome.benefit.b.audience": "Für Eltern",
    "welcome.benefit.b.title": "Weniger Organisationsstress",
    "welcome.benefit.b.text":
      "Ein gemeinsamer Überblick ersetzt lange Nachrichtenketten und spontane Absprachen.",
    "welcome.benefit.c.audience": "Für alle",
    "welcome.benefit.c.title": "Mehr Gemeinschaft",
    "welcome.benefit.c.text":
      "Eltern unterstützen sich gegenseitig – und Kinder starten gemeinsam in den Tag.",
    "welcome.join.title": "Lust auf einen gemeinschaftlichen Schulweg?",
    "welcome.join.text":
      "Du möchtest schoolescort mit deiner Klasse oder Nachbarschaft ausprobieren? Registriere dich direkt in der App.",
    "welcome.join.join": "Jetzt registrieren",
    "welcome.join.hint": "Unverbindlich · Dauert nur einen Moment",
    "welcome.faq.sectionLabel": "Gut zu wissen",
    "welcome.faq.sectionTitle": "Häufige Fragen",
    "welcome.faq.q1.q": "Für wen ist schoolescort gedacht?",
    "welcome.faq.q1.a":
      "Für Eltern und Bezugspersonen, die den Schulweg mit dem Fahrrad gemeinsam organisieren möchten – zum Beispiel innerhalb einer Klasse oder Nachbarschaft.",
    "welcome.faq.q2.q": "Muss ich jeden Morgen begleiten?",
    "welcome.faq.q2.a":
      "Nein – die Idee ist, dass sich die beteiligten Familien nach ihren Möglichkeiten abwechseln und die Begleitung gemeinsam abstimmen.",
    "welcome.faq.q3.q": "Organisiert schoolescort die Route automatisch?",
    "welcome.faq.q3.a":
      "schoolescort befindet sich im Aufbau. Automatische Routenplanung ist bisher nicht umgesetzt – die Seite zeigt das Konzept und den Weg zur Registrierung.",
    "welcome.faq.q4.q": "Was passiert nach der Registrierung?",
    "welcome.faq.q4.a":
      "Du legst ein Konto an und bestätigst deine E-Mail-Adresse. Danach kannst du eine Gruppe erstellen oder dich mit einem Einladungscode einer bestehenden Gruppe anschließen.",
    "welcome.footer.tagline": "Gemeinsam sicherer zur Schule radeln.",
    "welcome.footer.faq": "FAQ",
    "welcome.footer.note":
      "© {year} schoolescort · Konzeptseite – Platzhalter für Impressum und Datenschutz; Registrierung und E-Mail-Kontakt sind hinterlegt.",
  },
  en: {
    // common.* — general, cross-page texts
    "common.appName": "Schoolescort-Planner",
    "common.loading": "Loading …",
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.close": "Close",
    "common.back": "Back",
    "common.confirm": "Confirm",
    "common.retry": "Try again",

    // languageSwitcher.* — language switcher
    "languageSwitcher.groupAria": "Select language",

    // themeModeSwitcher.* — theme mode switcher
    "themeModeSwitcher.auto": "Color scheme: automatic (follow system)",
    "themeModeSwitcher.light": "Color scheme: light",
    "themeModeSwitcher.dark": "Color scheme: dark",

    // landing.* — sign-in / registration page
    "landing.tab.login": "Login",
    "landing.tab.register": "Register",
    "landing.field.name": "Name",
    "landing.field.email": "Email",
    "landing.field.password": "Password",
    "landing.submit.signingIn": "Signing in…",
    "landing.submit.registering": "Registering…",
    "landing.submit.signIn": "Sign in",
    "landing.submit.createAccount": "Create account",
    "landing.link.whatIs": "What is schoolescort?",
    // landing.register.* — hints after registration / verification
    "landing.register.checkEmail": "Please confirm your email — we've sent you a link.",
    "landing.login.emailNotVerified": "Please confirm your email address first to sign in.",
    "landing.login.resend": "Resend email",

    // groupList.* — groups overview
    "groupList.title": "My Groups",
    "groupList.action.profile": "Profile",
    "groupList.profileSubtitle": "Children & email notifications",
    "groupList.profileAria": "Profile",
    "groupList.action.createGroup": "Create Group",
    "groupList.action.joinGroup": "Join Group",
    "groupList.action.createHint": "Start a new group",
    "groupList.action.joinHint": "With an invite code",
    "groupList.viewPlan": "View Plan",
    "groupList.viewPlanAria": "{name}: View Plan",
    "groupList.loading": "Loading groups…",
    "groupList.empty.title": "No groups yet",
    "groupList.empty.hint": "Create a new group or join one with an invitation code.",
    "groupList.signOut": "Sign out",
    // groupList.create.* — create group form
    "groupList.create.title": "Create New Group",
    "groupList.create.nameLabel": "Group Name",
    "groupList.create.namePlaceholder": "e.g. Morning Walkers",
    "groupList.create.nameRequired": "Group name is required.",
    "groupList.create.submitting": "Creating…",
    "groupList.create.submit": "Create Group",
    // groupList.join.* — join group form
    "groupList.join.title": "Join a Group",
    "groupList.join.codeLabel": "Invitation Code",
    "groupList.join.codePlaceholder": "e.g. AB12CD34",
    "groupList.join.codeRequired": "Invitation code is required.",
    "groupList.join.submitting": "Joining…",
    "groupList.join.submit": "Join Group",
    // groupList.defaults.* — default summary on the group card
    "groupList.defaults.label": "Default",

    // groupSettings.* — group settings
    "groupSettings.title": "Group Settings",
    "groupSettings.titleNamed": "{name} — Settings",
    "groupSettings.back": "← Back to groups",
    "groupSettings.loading": "Loading group…",
    "groupSettings.loadError": "Failed to load the group. Please refresh.",
    // groupSettings.invite.* — invitation code
    "groupSettings.invite.title": "Invitation Code",
    "groupSettings.invite.copy": "Copy",
    "groupSettings.invite.copied": "Copied!",
    "groupSettings.invite.copyAria": "Copy invitation code",
    "groupSettings.invite.regenerate": "Regenerate code",
    "groupSettings.invite.regenerating": "Regenerating…",
    "groupSettings.invite.hint": "Share this code so others can join the group.",
    "groupSettings.invite.hintAdmin": " Regenerating invalidates the previous code.",
    "groupSettings.invite.regenerateError": "Failed to regenerate the code. Please try again.",
    // groupSettings.members.* — member management
    "groupSettings.members.title": "Members ({count})",
    "groupSettings.members.you": " (you)",
    "groupSettings.members.role.admin": "admin",
    "groupSettings.members.role.member": "member",
    "groupSettings.members.remove": "Remove",
    "groupSettings.members.removing": "Removing…",
    "groupSettings.members.removeAria": "Remove {name}",
    "groupSettings.members.removeError": "Failed to remove the member. Please try again.",
    // groupSettings.delete.* — delete group
    "groupSettings.delete.title": "Delete group",
    "groupSettings.delete.button": "Delete group",
    "groupSettings.delete.warning": "This group and all weekly plans will be permanently deleted.",
    // groupSettings.retention.* — retention of old weekly plans
    "groupSettings.retention.title": "Automatically delete past weekly plans",
    "groupSettings.retention.immediate": "Immediately",
    "groupSettings.retention.1w": "After 1 week",
    "groupSettings.retention.1m": "After 1 month",
    "groupSettings.retention.3m": "After 3 months",
    "groupSettings.retention.adminOnlyHint":
      "Only admins can change this setting.",
    // groupSettings.notifyOnChange.* — prompt to notify after changes
    "groupSettings.notifyOnChange.title": "Prompt to notify after changes",
    "groupSettings.notifyOnChange.label": "Ask members when leaving the weekly plan",
    "groupSettings.notifyOnChange.hint":
      "When enabled, members are asked after changing the weekly plan whether the group should be notified.",
    "groupSettings.notifyOnChange.adminOnlyHint":
      "Only admins can change this setting.",
    // groupSettings.defaults.* — group default trip details
    "groupSettings.defaults.title": "Default trip details",
    "groupSettings.defaults.hint": "These values pre-fill new days and can be applied to future days.",
    "groupSettings.defaults.morning.title": "Morning",
    "groupSettings.defaults.afternoon.title": "Afternoon",
    "groupSettings.defaults.startTime": "Start time",
    "groupSettings.defaults.transport": "Transport mode",
    "groupSettings.defaults.transport.aria": "Default transport mode for {direction}",
    "groupSettings.defaults.meetingPoint": "Meeting point",
    "groupSettings.defaults.save": "Save",
    "groupSettings.defaults.saveDialog.title": "Save defaults",
    "groupSettings.defaults.saveDialog.overwrite": "Overwrite all future days",
    "groupSettings.defaults.saveDialog.fillEmpty": "Fill empty fields only",
    "groupSettings.defaults.saveDialog.cancel": "Cancel",
    "groupSettings.defaults.saved": "Default values saved.",
    "groupSettings.defaults.saveError": "Failed to save the default values.",

    // profile.* — profile page (notifications, availability, children)
    "profile.title": "Profile",
    "profile.back": "← Back to groups",
    "profile.groups.loading": "Loading your groups…",
    "profile.groups.loadError": "Failed to load your groups. Please refresh.",
    "profile.groups.empty": "You are not a member of any groups yet. Join or create a group to manage notification preferences and availability.",
    // profile.children.* — "My children"
    "profile.children.title": "My children",
    "profile.children.note": "Manage the children you are responsible for escorting.",
    "profile.children.loading": "Loading your children…",
    "profile.children.loadError": "Failed to load your children. Please refresh.",
    "profile.children.empty": "You have not added any children yet.",
    "profile.children.newNameAria": "New child name",
    "profile.children.newNamePlaceholder": "Child's name",
    "profile.children.adding": "Adding…",
    "profile.children.add": "Add child",
    "profile.children.addError": "Failed to add child. Please try again.",
    "profile.children.nameRequired": "Please enter a name.",
    // profile.child.* — single child row
    "profile.child.renameAria": "Rename {name}",
    "profile.child.saving": "Saving…",
    "profile.child.rename": "Rename",
    "profile.child.removing": "Removing…",
    "profile.child.remove": "Remove",
    "profile.child.renameError": "Failed to rename. Please try again.",
    "profile.child.removeError": "Failed to remove. Please try again.",
    "profile.child.nameEmpty": "Name cannot be empty.",
    // profile.notifications.* — notification preferences
    "profile.notifications.title": "Notification preferences",
    "profile.notifications.note": "Choose how you want to be notified per group.",
    "profile.notifications.email": "Email notifications",
    "profile.notifications.push": "Push notifications",
    "profile.notifications.saved": "Preference saved.",
    "profile.notifications.saveError": "Could not save. Please try again.",
    "profile.notifications.loading": "Loading settings …",
    "profile.notifications.loadError": "Could not load settings.",
    // profile.availability.* — availability
    "profile.availability.title": "Availability",
    "profile.availability.note": "Mark dates when you are unavailable to escort. Entries you add here are tracked for this session only.",
    "profile.availability.dateLabel": "Date",
    "profile.availability.saving": "Saving…",
    "profile.availability.markUnavailable": "Mark unavailable",
    "profile.availability.scheduled": "Scheduled",
    "profile.availability.remove": "Remove",
    "profile.availability.empty": "No dates marked unavailable this session.",
    "profile.availability.pickDate": "Please pick a date first.",
    "profile.availability.alreadyMarked": "That date is already marked unavailable.",
    "profile.availability.markError": "Failed to mark the date unavailable. Please try again.",
    "profile.availability.removeError": "Failed to remove the entry. Please try again.",

    // weeklyPlan.* — weekly plan view
    "weeklyPlan.title": "Weekly Plan",
    "weeklyPlan.header.settings": "⚙ Settings & invite",
    "weeklyPlan.header.notify": "📣 Notify group",
    "weeklyPlan.header.notifyAria": "Notify group",
    "weeklyPlan.header.backToGroups": "← Back to groups",
    // weeklyPlan.nav.* — week navigation
    "weeklyPlan.nav.prev": "← Prev",
    "weeklyPlan.nav.next": "Next →",
    "weeklyPlan.nav.weekLabel": "Week {week}",
    "weeklyPlan.nav.rangeSep": " to ",
    "weeklyPlan.nav.rangeSepShort": "–",
    // weeklyPlan.* — loading / empty / error states
    "weeklyPlan.loading": "Loading plan…",
    "weeklyPlan.loadError": "Failed to load the plan. Please refresh.",
    "weeklyPlan.empty.title": "No plan for this week",
    "weeklyPlan.empty.hint": "There is no escort plan for {week} yet.",
    "weeklyPlan.create.submit": "Create Plan",
    "weeklyPlan.create.submitting": "Creating…",
    "weeklyPlan.create.error": "Failed to create the plan. Please try again.",
    // weeklyPlan.delete.* — delete weekly plan
    "weeklyPlan.delete.button": "Delete week",
    "weeklyPlan.delete.warning": "This weekly plan will be permanently deleted.",
    // weeklyPlan.notifyOnChange.* — "notify on leave" prompt
    "weeklyPlan.notifyOnChange.title": "Notify the group?",
    "weeklyPlan.notifyOnChange.message":
      "You changed the weekly plan. Should the other members be notified about the changes?",
    "weeklyPlan.notifyOnChange.confirm": "Notify",
    "weeklyPlan.notifyOnChange.decline": "Don't notify",
    // weeklyPlan.day.* — weekdays (Mon–Fri)
    "weeklyPlan.day.mon": "Mon",
    "weeklyPlan.day.tue": "Tue",
    "weeklyPlan.day.wed": "Wed",
    "weeklyPlan.day.thu": "Thu",
    "weeklyPlan.day.fri": "Fri",
    // weeklyPlan.direction.* — trip directions
    "weeklyPlan.direction.morning": "Morning",
    "weeklyPlan.direction.afternoon": "Afternoon",
    // weeklyPlan.transport.* — transport modes (display; API values stay unchanged)
    "weeklyPlan.transport.bike": "🚲 Bike",
    "weeklyPlan.transport.car": "🚗 Car",
    "weeklyPlan.transport.bus": "🚌 Bus",
    "weeklyPlan.transport.aria": "Transport mode for {direction}",
    // weeklyPlan.status.* — availability labels
    "weeklyPlan.status.wish": "I want to ride",
    "weeklyPlan.status.optional": "I could ride",
    "weeklyPlan.status.blocker": "I can't ride",
    "weeklyPlan.status.open": "Open",
    "weeklyPlan.status.clear": "Clear",
    "weeklyPlan.status.ownAria": "Your availability for {direction}",
    "weeklyPlan.status.memberAria": "Set availability for {name}",
    // weeklyPlan.summary.* — summary (hero box + counters)
    "weeklyPlan.summary.wish": "I want to ride: {count} trips",
    "weeklyPlan.summary.wishFor": "{name} · {count} trips (wants to ride)",
    "weeklyPlan.summary.sub": "Could: {optional} · Can't: {blocker} · Still open: {open}",
    // weeklyPlan.admin.* — admin view & "editing for"
    "weeklyPlan.admin.view": "Admin view",
    "weeklyPlan.admin.editingFor": "You're editing for {name}",
    "weeklyPlan.admin.end": "Stop",
    "weeklyPlan.admin.setFor": "Status set for {name}",
    "weeklyPlan.admin.lockAria": "Set by admin",
    "weeklyPlan.admin.selfLabel": "Me",
    // weeklyPlan.trip.* — trip details
    "weeklyPlan.trip.startTimeAria": "Start time for {direction}",
    "weeklyPlan.trip.meetingPoint": "Meeting point",
    "weeklyPlan.trip.meetingPointAria": "Meeting point for {direction}",
    "weeklyPlan.trip.noResponses": "No responses yet",
    "weeklyPlan.you": "You",
    // weeklyPlan.children.* — children area
    "weeklyPlan.children.label": "Children",
    "weeklyPlan.children.empty": "No children yet",
    "weeklyPlan.children.ownAria": "Your children for {direction}",
    "weeklyPlan.children.add": "＋ {name}",
    "weeklyPlan.children.participating": "✓ {name}",
    "weeklyPlan.children.removeAria": "Remove {name} from {direction}",
    "weeklyPlan.children.remove": "Remove",
    "weeklyPlan.children.noneHint": "No children added yet.",
    "weeklyPlan.children.createLink": "Create children here",
    // weeklyPlan.manage.* — admin management
    "weeklyPlan.manage.show": "▸ Manage members",
    "weeklyPlan.manage.hide": "▾ Hide members",
    "weeklyPlan.manage.noMembers": "No active members.",
    // weeklyPlan.trip.details — trip details panel
    "weeklyPlan.trip.details": "Details",
    "weeklyPlan.trip.detailsAria": "Open details for {direction}",
    "weeklyPlan.trip.who": "Who's coming?",
    "weeklyPlan.trip.passengers": "Passengers",
    "weeklyPlan.trip.onTheWay": "On the way",
    "weeklyPlan.trip.blocked": "Can't ride",
    "weeklyPlan.trip.substitutes": "Substitutes",
    // weeklyPlan.comments.* — comments area (internal: notes; display: comments)
    "weeklyPlan.comments.label": "Comments",
    "weeklyPlan.comments.empty": "No comments yet",
    "weeklyPlan.comments.sectionAria": "Comments for {label}",
    "weeklyPlan.comments.openAria": "Open comments for {label}",
    "weeklyPlan.comments.deleteAria": "Delete comment",
    "weeklyPlan.comments.delete": "Delete",
    "weeklyPlan.comments.placeholder": "Add a comment…",
    "weeklyPlan.comments.addAria": "Add a comment for {label}",
    "weeklyPlan.comments.add": "Add",
    "weeklyPlan.comments.done": "Done",
    // weeklyPlan.notes.* — notes area (internal names kept)
    "weeklyPlan.notes.label": "Notes",
    "weeklyPlan.notes.empty": "No notes yet",
    "weeklyPlan.notes.sectionAria": "Notes for {label}",
    "weeklyPlan.notes.deleteAria": "Delete note",
    "weeklyPlan.notes.delete": "Delete",
    "weeklyPlan.notes.placeholder": "Add a note…",
    "weeklyPlan.notes.addAria": "Add a note for {label}",
    "weeklyPlan.notes.add": "Add note",
    // weeklyPlan.toast.* — transient success messages
    "weeklyPlan.toast.modeUpdated": "Transport mode updated.",
    "weeklyPlan.toast.detailsUpdated": "Trip details updated.",
    "weeklyPlan.toast.childrenUpdated": "Children updated.",
    "weeklyPlan.toast.availabilityUpdated": "Availability updated.",
    "weeklyPlan.toast.noteAdded": "Note added.",
    "weeklyPlan.toast.noteDeleted": "Note deleted.",
    "weeklyPlan.toast.notified": "Group notified.",
    "weeklyPlan.toast.weekDeleted": "Weekly plan deleted.",
    "weeklyPlan.toast.notifyError": "Could not notify the group. Please try again.",

    // editEntry.* — escort assignment dialog
    "editEntry.ariaLabel": "Edit escort assignment",
    "editEntry.title": "Edit assignment",
    "editEntry.loadingMembers": "Loading members…",
    "editEntry.loadMembersError": "Failed to load assignable members.",
    "editEntry.escortLabel": "Escort",
    "editEntry.unassigned": "Unassigned",
    "editEntry.saving": "Saving…",
    "editEntry.saveError": "Failed to save the assignment. Please try again.",

    // verifyEmail.* — email confirmation page (Req 7.1)
    "verifyEmail.title": "Confirm email",
    "verifyEmail.loading": "Confirming your email address …",
    "verifyEmail.success": "Your email address has been confirmed. You can sign in now.",
    "verifyEmail.error": "Confirmation failed. Please request a new email.",
    "verifyEmail.emailLabel": "Email",
    "verifyEmail.emailPlaceholder": "you@email.com",
    "verifyEmail.resendButton": "Resend email",
    "verifyEmail.resending": "Sending …",
    "verifyEmail.resendSnackbar": "If an account exists, we've sent you a new confirmation email.",

    // errors.* — error base incl. generic fallback (Req 12.4/12.5)
    "errors.fallback": "An error occurred. Please try again.",
    "errors.UNAUTHENTICATED": "Please sign in.",
    "errors.VALIDATION_ERROR": "The input is invalid. Please check it.",
    "errors.SERVER_ERROR": "A server error occurred. Please try again later.",
    // errors.* — mapped backend error codes (Req 12.1/12.2)
    "errors.INVALID_CREDENTIALS": "The email address or password is incorrect.",
    "errors.INVALID_TOKEN": "Your session has expired. Please sign in again.",
    "errors.DUPLICATE_EMAIL": "This email address is already registered.",
    "errors.NOT_FOUND": "The requested item could not be found.",
    "errors.NOT_GROUP_ADMIN": "You must be an admin of this group to do that.",
    "errors.UNAUTHORIZED_MODIFICATION": "You are not allowed to make this change.",
    "errors.PAST_TRIP_MODIFICATION": "Past days can no longer be changed.",
    "errors.PAST_ENTRY_MODIFICATION": "Past entries can no longer be changed.",
    "errors.INVALID_ISO_WEEK": "The specified calendar week is invalid.",
    "errors.WEEK_OUT_OF_RANGE": "The selected week is outside the allowed range.",
    "errors.DUPLICATE_PLAN": "A plan already exists for this week.",
    "errors.INVALID_INVITATION_CODE": "No group was found for this invitation code.",
    "errors.EMAIL_NOT_VERIFIED": "Please confirm your email address first to sign in.",
    "errors.INVALID_VERIFICATION_TOKEN": "This confirmation link is invalid or has expired. Please request a new email.",

    // welcome.* — welcome page (/welcome)
    "welcome.logo.alt": "Schoolescort logo",
    "welcome.nav.howItWorks": "How it works",
    "welcome.nav.benefits": "Benefits",
    "welcome.nav.faq": "FAQ",
    "welcome.nav.join": "Join in",
    "welcome.hero.badge": "Together instead of alone",
    "welcome.hero.titleBefore": "The school run becomes",
    "welcome.hero.titleAccent": "teamwork.",
    "welcome.hero.subtitle":
      "With schoolescort, parents take turns escorting the children by bike. Children ride to school together – and the responsibility is shared across several shoulders.",
    "welcome.hero.join": "Start with other families",
    "welcome.hero.howItWorks": "How it works",
    "welcome.hero.checkNote": "Easy to organise · On the road together",
    "welcome.how.sectionLabel": "How it works",
    "welcome.how.sectionTitle": "Less coordinating. More riding together.",
    "welcome.how.sectionText":
      "schoolescort brings families together and helps organise the school run fairly and clearly.",
    "welcome.how.step1.title": "Form a group",
    "welcome.how.step1.text":
      "A few families from the class or neighbourhood team up.",
    "welcome.how.step2.title": "Agree on escorts",
    "welcome.how.step2.text":
      "Together you decide who rides along with the children on which morning.",
    "welcome.how.step3.title": "Ride off together",
    "welcome.how.step3.text":
      "The children ride to school safely and accompanied in a familiar group.",
    "welcome.strip.title": "A more relaxed start to the school day.",
    "welcome.strip.a.title": "Share fairly",
    "welcome.strip.a.text": "Escort duty takes turns",
    "welcome.strip.b.title": "Ride together",
    "welcome.strip.b.text": "Children experience the route as a group",
    "welcome.strip.c.title": "Agree clearly",
    "welcome.strip.c.text": "Everyone knows who is up when",
    "welcome.benefit.sectionLabel": "Why schoolescort?",
    "welcome.benefit.sectionTitle": "Good for families. Good for mornings.",
    "welcome.benefit.sectionText":
      "When several families team up, the same person no longer has to escort every morning. A daily task becomes a shared project.",
    "welcome.benefit.cta": "Get started together",
    "welcome.benefit.a.audience": "For children",
    "welcome.benefit.a.title": "Accompanied on the way",
    "welcome.benefit.a.text":
      "The children ride to school in a familiar group with an adult escort.",
    "welcome.benefit.b.audience": "For parents",
    "welcome.benefit.b.title": "Less organisational stress",
    "welcome.benefit.b.text":
      "A shared overview replaces long message threads and ad-hoc arrangements.",
    "welcome.benefit.c.audience": "For everyone",
    "welcome.benefit.c.title": "More community",
    "welcome.benefit.c.text":
      "Parents support each other – and children start the day together.",
    "welcome.join.title": "Fancy a community school run?",
    "welcome.join.text":
      "Would you like to try schoolescort with your class or neighbourhood? Register directly in the app.",
    "welcome.join.join": "Register now",
    "welcome.join.hint": "Non-binding · Only takes a moment",
    "welcome.faq.sectionLabel": "Good to know",
    "welcome.faq.sectionTitle": "Frequently asked questions",
    "welcome.faq.q1.q": "Who is schoolescort for?",
    "welcome.faq.q1.a":
      "For parents and guardians who want to organise the bike ride to school together – for example within a class or neighbourhood.",
    "welcome.faq.q2.q": "Do I have to escort every morning?",
    "welcome.faq.q2.a":
      "No – the idea is that the participating families take turns according to their availability and coordinate the escorting together.",
    "welcome.faq.q3.q": "Does schoolescort plan the route automatically?",
    "welcome.faq.q3.a":
      "schoolescort is under construction. Automatic route planning has not been implemented yet – this page presents the concept and the way to register.",
    "welcome.faq.q4.q": "What happens after registering?",
    "welcome.faq.q4.a":
      "You create an account and confirm your email address. You can then create a group or join an existing one with an invitation code.",
    "welcome.footer.tagline": "Riding to school more safely together.",
    "welcome.footer.faq": "FAQ",
    "welcome.footer.note":
      "© {year} schoolescort · Concept page – placeholders for legal notice and privacy; registration and email contact are linked.",
  },
};
